import * as Dialog from "@radix-ui/react-dialog";
import { useAtom } from "jotai";
import { X } from "lucide-react";
import { useEffect } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";

import { Header } from "@app/layout/Header.tsx";
import { SidebarNav } from "@app/layout/Sidebar.tsx";
import { getVersionEntry } from "@lib/content.ts";
import { mobileNavOpenAtom } from "@lib/state.ts";

export function DocsLayout() {
  const { version = "" } = useParams();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useAtom(mobileNavOpenAtom);
  const entry = getVersionEntry(version);

  // A deep link should land at the top of its page, not wherever the previous
  // page happened to be scrolled to. Anchor links keep their native behaviour.
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname, location.hash]);

  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <Header version={version} />

      {entry && entry.status !== "stable" ? (
        <div
          role="status"
          className="border-b border-line bg-warning-tint px-4 py-2 text-center text-[13px] text-warning"
        >
          {entry.status === "beta"
            ? `${entry.label} is in beta — endpoints and payloads may still change.`
            : `${entry.label} is ${entry.status}.`}
          {entry.sunsetAt
            ? ` Scheduled for removal on ${entry.sunsetAt}.`
            : null}
        </div>
      ) : null}

      <div className="mx-auto flex w-full max-w-[110rem]">
        {/* Left rail ---------------------------------------------------- */}
        <aside
          className="gt-scroll hidden shrink-0 overflow-y-auto border-r border-line bg-rail px-3 py-5 lg:block"
          style={{
            width: "var(--gt-rail-w)",
            height: "calc(100vh - var(--gt-header-h))",
            position: "sticky",
            top: "var(--gt-header-h)",
          }}
        >
          <SidebarNav version={version} />
        </aside>

        {/* Centre + right ---------------------------------------------- */}
        <main id="main" className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer ------------------------------------------------- */}
      <Dialog.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 lg:hidden" />
          <Dialog.Content
            aria-label="Navigation"
            className="gt-scroll fixed inset-y-0 left-0 z-50 w-[min(19rem,85vw)] overflow-y-auto border-r border-line bg-bg px-3 py-4 lg:hidden"
          >
            <div className="mb-3 flex items-center justify-between px-2">
              <Dialog.Title className="text-[13px] font-semibold text-ink">
                Documentation
              </Dialog.Title>
              <Dialog.Close
                aria-label="Close navigation"
                className="rounded-sm2 p-1 text-muted hover:bg-surface-2 hover:text-ink"
              >
                <X className="size-4" aria-hidden />
              </Dialog.Close>
            </div>
            <SidebarNav
              version={version}
              onNavigate={() => setMobileNavOpen(false)}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
